/**
 * Sync Service - The Background Sync Engine
 *
 * Handles synchronization of local IndexedDB events to the server.
 * Runs in the background, retries on failure, handles offline gracefully.
 *
 * Flow:
 * 1. User punches → Saved to IndexedDB immediately
 * 2. SyncService detects pending events
 * 3. Batches and sends to /api/v1/sync/batch
 * 4. On success: marks as synced
 * 5. On failure: marks as error, retries later
 */
import {
  db,
  cleanupOldEvents,
  getPendingEvents,
  markEventsSynced,
  markEventsError,
  marcarEventosARevision,
  recuperarEventosAtascados,
  reactivarEventosDescartados,
  PunchEvent
} from '../db/database';
import { apiClient } from './apiClient';

// =============================================================================
// Types
// =============================================================================

interface BatchSyncRequest {
  events: Array<{
    client_event_id: string;
    timestamp: string;
    event_type: string;
    break_reason?: string;
    worksite_uuid?: string;
    latitude?: number;
    longitude?: number;
    accuracy?: number;
    qr_token?: string;
    work_mode?: string;
    device_fingerprint?: string;
    // Señales antifraude calculadas en el dispositivo (ronda 3, r2b18[10]):
    // contrato fijo con el backend, que las acepta y las persiste.
    fraud_flags?: string[];
  }>;
  client_timezone: string;
}

/** Los valores de fraud_flags que admite el contrato de /sync/batch. */
const FRAUD_FLAGS_DEL_CONTRATO: ReadonlySet<string> = new Set([
  'MOCK_LOCATION',
  'EMULATOR_DETECTED',
  'TIMEZONE_MISMATCH',
  'IMPOSSIBLE_SPEED',
  'LOW_ACCURACY',
]);

/**
 * Deja las flags listas para el payload: sólo valores del contrato y como
 * máximo 10 (así lo fija el schema del backend). Lo que no entra —p. ej. un
 * valor nuevo todavía no admitido— no tumba el lote entero.
 */
function fraudFlagsParaElPayload(flags?: string[]): string[] | undefined {
  if (!flags || flags.length === 0) return undefined;
  const admitidas = flags.filter((f) => FRAUD_FLAGS_DEL_CONTRATO.has(f)).slice(0, 10);
  return admitidas.length > 0 ? admitidas : undefined;
}

interface BatchSyncResponse {
  success: boolean;
  processed_count: number;
  failed_count: number;
  results: Array<{
    client_event_id: string;
    success: boolean;
    server_id?: number;
    // Veredicto del servidor por marca: VALID cuenta ya;
    // PENDING_APPROVAL está en el ledger pero NO cuenta hasta que un
    // responsable la apruebe (geovalla sin comprobar, token de pantalla
    // caducado, retrodatación…). El backend lo devuelve desde la ronda 2
    // y aquí no se miraba (ronda 3, r2b02/r2b18).
    status?: 'VALID' | 'PENDING_APPROVAL';
    review_reason?: string;
    error_code?: string;
    error_message?: string;
  }>;
  server_timestamp: string;
  rollback_reason?: string;
}

// =============================================================================
// Sync Service Class
// =============================================================================

class SyncService {
  private isRunning = false;
  private syncInterval: number | null = null;
  // De quién es la cola que se está subiendo. Sin esto, `sync()` cogía TODOS
  // los pendientes del dispositivo y los mandaba con el token de quien tuviera
  // la sesión abierta: el fichaje de A entraba en la jornada de B.
  private userId: number | null = null;

  // `sync()` lo disparan cuatro cosas: el intervalo de 30 s, volver a tener red,
  // volver a primer plano y el arranque. Sin exclusión, dos de ellas a la vez
  // leen la misma cola y mandan el mismo evento dos veces.
  private sincronizando = false;

  private readonly SYNC_INTERVAL_MS = 30000; // 30 seconds
  private readonly MAX_BATCH_SIZE = 50;

  /**
   * Start the sync service.
   * Should be called when app initializes and user is authenticated.
   */
  start(userId: number): void {
    // Si ya corría para otra persona, se para primero: la cola es por usuario
    // y el token que la firma es el de la sesión viva.
    if (this.isRunning && this.userId !== userId) this.stop();
    if (this.isRunning) return;

    this.userId = userId;

    // Lo que se quedó a medias en un intento anterior vuelve a la cola: si no,
    // no se reintenta nunca y desaparece sin ruido.
    void recuperarEventosAtascados(userId);

    // Y lo ya sincronizado de hace más de 30 días se borra del dispositivo.
    // `cleanupOldEvents` existía y NO la llamaba nadie: cada fichaje con su
    // coordenada y su precisión se quedaba en IndexedDB para siempre, aunque
    // el servidor lo hubiera anonimizado a los 12 meses. La política publicada
    // promete minimización y en el móvil del trabajador no había ningún reloj
    // que la ejecutara.
    void cleanupOldEvents();

    console.log('[Sync] Starting sync service...');
    this.isRunning = true;

    // Initial sync
    this.sync();

    // Periodic sync
    this.syncInterval = window.setInterval(() => {
      this.sync();
    }, this.SYNC_INTERVAL_MS);

    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);

    // Listen for visibility changes (sync when app comes to foreground)
    document.addEventListener('visibilitychange', this.handleVisibility);

    console.log('[Sync] Sync service started');
  }

  /**
   * Stop the sync service.
   * Call when user logs out.
   */
  stop(): void {
    if (!this.isRunning) return;

    console.log('[Sync] Stopping sync service...');
    this.isRunning = false;
    this.userId = null;

    if (this.syncInterval) {
      window.clearInterval(this.syncInterval);
      this.syncInterval = null;
    }

    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    document.removeEventListener('visibilitychange', this.handleVisibility);

    console.log('[Sync] Sync service stopped');
  }

  /**
   * Main sync function.
   * Gets pending events and sends them to server.
   */
  async sync(): Promise<void> {
    // El contador de "en cola" del store no lo escribía NADIE: `getStatus`
    // calculaba el número de verdad y no tenía llamante, así que la pantalla
    // prometía "N en cola" y enseñaba siempre 0. Con doce fichajes sin subir,
    // la única señal de "tienes trabajo pendiente" era invisible.
    void this.publicarEstado();

    if (!navigator.onLine) {
      console.log('[Sync] Offline - skipping sync');
      return;
    }

    if (this.userId === null) {
      return; // Sin sesión no se sube nada de nadie.
    }
    if (this.sincronizando) {
      return;
    }
    this.sincronizando = true;

    try {
      const pendingEvents = await getPendingEvents(this.userId);

      if (pendingEvents.length === 0) {
        return; // Nothing to sync
      }

      console.log(`[Sync] Found ${pendingEvents.length} pending events`);

      // Process in batches
      for (let i = 0; i < pendingEvents.length; i += this.MAX_BATCH_SIZE) {
        const batch = pendingEvents.slice(i, i + this.MAX_BATCH_SIZE);
        await this.syncBatch(batch);
      }
    } catch (error) {
      console.error('[Sync] Sync failed:', error);
    } finally {
      this.sincronizando = false;
    }
  }

  /**
   * Sync a batch of events to the server.
   */
  private async syncBatch(events: PunchEvent[]): Promise<void> {
    // Mark as syncing
    const clientIds = events.map(e => e.clientEventId);
    await db.punchEvents
      .where('clientEventId')
      .anyOf(clientIds)
      .modify({ syncStatus: 'syncing' });

    try {
      const request: BatchSyncRequest = {
        events: events.map(e => ({
          client_event_id: e.clientEventId,
          timestamp: e.timestamp.toISOString(),
          event_type: e.eventType,
          break_reason: e.breakReason,
          worksite_uuid: e.worksiteUuid,
          latitude: e.latitude,
          longitude: e.longitude,
          accuracy: e.accuracy != null ? Math.round(e.accuracy) : undefined,
          qr_token: e.qrToken,
          work_mode: e.workMode ?? 'ONSITE',
          device_fingerprint: e.deviceFingerprint,
          fraud_flags: fraudFlagsParaElPayload(e.fraudFlags)
        })),
        client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      const response = await apiClient.post<BatchSyncResponse>(
        '/sync/batch',
        request
      );

      if (response.data.success) {
        // All succeeded - mark as synced
        const serverIds = new Map<string, number>();
        const aRevision = new Map<string, string | undefined>();
        for (const result of response.data.results) {
          if (result.server_id) {
            serverIds.set(result.client_event_id, result.server_id);
          }
          if (result.status === 'PENDING_APPROVAL') {
            aRevision.set(result.client_event_id, result.review_reason);
          }
        }
        await markEventsSynced(clientIds, serverIds);
        await marcarEventosARevision(aRevision);
        if (aRevision.size > 0) {
          // "Registrada" era verdad a medias: la marca está en el ledger pero
          // la jornada no cuenta hasta que un responsable la apruebe. El
          // trabajador lo sabe EN el momento, no semanas después.
          const { useAppStore } = await import('../store/useAppStore');
          useAppStore.getState().showToast(
            `${aRevision.size} marca(s) registrada(s) a la espera de revisión: no cuentan hasta que tu responsable las apruebe`,
            'info'
          );
        }
        // Tras subir, la pantalla se reconcilia con el estado REAL del
        // servidor (ronda 3, r2b18[8]): si alguna marca quedó a revisión o
        // el histórico local estaba desfasado, la pantalla deja de contar
        // hechos que el ledger no cuenta. No bloquea: es fuego y olvido.
        const { useAppStore } = await import('../store/useAppStore');
        void useAppStore.getState().reconcileWorkStateWithServer();
        console.log(`[Sync] Successfully synced ${events.length} events`);
      } else {
        // NO hay fallo parcial: `/sync/batch` es todo o nada y revierte el
        // lote entero. Marcar como sincronizados los que el servidor había
        // apuntado bien ANTES del rollback sacaba de la cola —para siempre—
        // fichajes que no existen en el ledger: un hueco silencioso en el
        // registro legal. Vuelven todos a la cola.
        //
        // Dead-letter sin colaterales (ronda 4, r2b18[3]): los resultados del
        // rollback señalan al culpable (conserva SU error_code de validación);
        // el resto cayó con él y es inocente. Sólo el culpable gasta intentos
        // — los inocentes vuelven a `error` SIN contar, o un solo evento
        // venenoso descartaba a los cinco intentos TODO su lote.
        const culpables = new Set(
          response.data.results
            .filter(
              (r) => !r.success && r.error_code && r.error_code !== 'BATCH_ROLLED_BACK'
            )
            .map((r) => r.client_event_id)
        );
        const mensaje = response.data.rollback_reason || 'El lote se revirtió entero';
        const culpablesIds = clientIds.filter((id) => culpables.has(id));
        const inocentesIds = clientIds.filter((id) => !culpables.has(id));
        if (culpablesIds.length > 0) {
          await markEventsError(culpablesIds, mensaje, true);
        }
        if (inocentesIds.length > 0) {
          await markEventsError(inocentesIds, mensaje, false);
        }
        console.log(`[Sync] Lote revertido: ${clientIds.length} eventos vuelven a la cola`);
      }
    } catch (error: any) {
      // Network error or server error.
      //
      // Aquí NO hay veredicto por-evento (ronda 4, r2b18[3]): un 422 del
      // schema Pydantic rechaza la PETICIÓN entera antes de entrar al
      // handler, un 409 de idempotencia y un 500 tampoco traen resultados.
      // Sin resultados no hay culpable identificable, y contar el intento a
      // TODOS mataba en ~2,5 min (5 ciclos de 30 s) a los fichajes buenos
      // que viajaban en el lote: dead-letter colateral. Se marca error SIN
      // contar intento y el lote se reintenta entero. Limitación honesta: si
      // hay un evento venenoso de verdad seguirá bloqueando SU lote hasta
      // que alguien lo quite de en medio — pero con el clamp de accuracy de
      // database.ts y el borde tolerante del schema, el caso real que lo
      // producía (accuracy > 10 000 de un fix por WiFi/celda) ya no dispara
      // un 422.
      const errorMessage = error.response?.data?.detail?.message || error.message || 'Unknown error';
      await markEventsError(clientIds, errorMessage, false);
      console.error('[Sync] Batch sync failed:', errorMessage);
    }
  }

  /**
   * Force immediate sync (useful after user action).
   */
  async forceSync(): Promise<void> {
    console.log('[Sync] Force sync requested');
    await this.sync();
  }

  /**
   * Reintento manual de los descartados (botón del banner de descartados).
   *
   * Los vuelve a poner en la cola y sincroniza ya, delante de los ojos de
   * quien decidió reintentarlos — no dentro de 30 s.
   */
  async reintentarDescartados(userId: number): Promise<void> {
    const reactivados = await reactivarEventosDescartados(userId);
    if (reactivados > 0) {
      console.log(`[Sync] Reintento manual de ${reactivados} eventos descartados`);
    }
    await this.sync();
  }

  /**
   * Handle coming back online.
   */
  private handleOnline = (): void => {
    console.log('[Sync] Back online - syncing...');
    this.sync();
  };

  /**
   * Handle going offline.
   */
  private handleOffline = (): void => {
    console.log('[Sync] Gone offline');
  };

  /**
   * Handle visibility change (app comes to foreground).
   */
  private handleVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      console.log('[Sync] App visible - syncing...');
      this.sync();
    }
  };

  /** Lleva el estado real de la cola al store, que es lo que pinta la pantalla. */
  private async publicarEstado(): Promise<void> {
    try {
      const estado = await this.getStatus();
      const { useAppStore } = await import('../store/useAppStore');
      useAppStore.getState().setSyncStatus({
        pendingCount: estado.pendingCount,
        errorCount: estado.errorCount,
        discardedCount: estado.discardedCount,
        isOnline: estado.isOnline,
      });
    } catch {
      /* el contador es informativo: si falla, no rompe el sync */
    }
  }

  /**
   * Get sync status for UI.
   */
  async getStatus(): Promise<{
    pendingCount: number;
    errorCount: number;
    discardedCount: number;
    isOnline: boolean;
  }> {
    // Contar SÓLO la cola de quien tiene la sesión (ronda 3, r2b18[11]):
    // `getPendingEvents` filtra por userId y este contador no, así que los
    // huérfanos de migraciones viejas (userId 0, que jamás se suben)
    // inflaban el «N en cola» para siempre. Sin sesión no hay cola que
    // publicar.
    if (this.userId === null) {
      return { pendingCount: 0, errorCount: 0, discardedCount: 0, isOnline: navigator.onLine };
    }
    const pending = await db.punchEvents
      .where('[userId+syncStatus]')
      .equals([this.userId, 'pending'])
      .count();
    const errors = await db.punchEvents
      .where('[userId+syncStatus]')
      .equals([this.userId, 'error'])
      .count();
    // Los descartados se cuentan aparte: no están "en cola", están apartados
    // esperando decisión humana — mezclarlos adormecería el contador igual
    // que antes (ronda 3, r2b18).
    const discarded = await db.punchEvents
      .where('[userId+syncStatus]')
      .equals([this.userId, 'discarded'])
      .count();

    return {
      pendingCount: pending,
      errorCount: errors,
      discardedCount: discarded,
      isOnline: navigator.onLine
    };
  }
}

// Singleton instance
export const syncService = new SyncService();
